import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';

// Preenchimento automático dos stories e dos pôsteres: pega os produtos da
// campanha, ordena por quem mais vende (últimos 90 dias, GET .../mais-vendidos)
// e devolve a lista pronta pra tela montar. Os mais vendidos vêm primeiro: no
// story eles saem sozinhos, em destaque; no pôster, nas primeiras páginas.

const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 });

/** Grupos de produtos por story: `sozinhos` primeiro, depois `duplas` produtos em pares, o resto de 3 em 3. */
export function gruposDeStory(lista, sozinhos, duplas) {
  const g = [];
  let i = 0;
  while (i < lista.length && g.length < sozinhos) g.push([lista[i++]]);
  const fimDuplas = Math.min(lista.length, i + duplas);
  while (i + 2 <= fimDuplas) { g.push(lista.slice(i, i + 2)); i += 2; }
  while (i < lista.length) { g.push(lista.slice(i, i + 3)); i += 3; }
  // Sobrou 1 sozinho no fim depois de um trio: vira duas duplas (fica mais equilibrado).
  const n = g.length;
  if (n >= 2 && g[n - 1].length === 1 && g[n - 2].length === 3) {
    const [a, b, c] = g[n - 2];
    g.splice(n - 2, 2, [a, b], [c, g[n - 1][0]]);
  }
  return g;
}

export default function PreenchimentoAuto({ tipo, campanhaId, produtos, jaUsados, temItens, onGerar, onFechar }) {
  const story = tipo === 'story';
  const [ranking, setRanking] = useState(null);   // Map codpro -> { valor, vendas }
  const [erroVendas, setErroVendas] = useState(false);
  const [quais, setQuais] = useState(jaUsados.size ? 'faltam' : 'todos');
  const [metrica, setMetrica] = useState('valor');
  const [limite, setLimite] = useState('');
  const [semFoto, setSemFoto] = useState(false);
  const [mostrarDe, setMostrarDe] = useState(true);
  const [substituir, setSubstituir] = useState(false);
  const [sozinhos, setSozinhos] = useState(3);
  const [duplas, setDuplas] = useState(4);
  const [modelo, setModelo] = useState('alternar');

  useEffect(() => {
    let vivo = true;
    axios.get(`${API}/catalogo/campanha/${campanhaId}/mais-vendidos`)
      .then(r => { if (vivo) setRanking(new Map((r.data.produtos || []).map(p => [p.codpro, p]))); })
      .catch(() => { if (vivo) { setErroVendas(true); setRanking(new Map()); } });
    return () => { vivo = false; };
  }, [campanhaId]);

  const ordenados = useMemo(() => {
    if (!ranking) return [];
    const nota = (p) => ranking.get(p.codpro)?.[metrica] || 0;
    const cands = produtos
      .filter(p => (quais === 'todos' || !jaUsados.has(p.codpro)) && (semFoto || p.temFoto))
      .sort((a, b) => nota(b) - nota(a) || (ranking.get(b.codpro)?.valor || 0) - (ranking.get(a.codpro)?.valor || 0));
    const n = parseInt(limite, 10);
    return n > 0 ? cands.slice(0, n) : cands;
  }, [ranking, produtos, quais, jaUsados, semFoto, metrica, limite]);

  const resumo = useMemo(() => {
    if (!story) {
      return `${ordenados.length} pôster(es), um por produto, do que mais vende pro que menos vende.`
        + (ordenados.length > 60 ? ` O PDF sai em ${Math.ceil(ordenados.length / 60)} arquivos de até 60.` : '');
    }
    const g = gruposDeStory(ordenados, sozinhos, duplas);
    const por = [1, 2, 3].map(k => g.filter(x => x.length === k).length);
    return `${g.length} story(s): ${por[0]} com 1 produto em destaque, ${por[1]} com 2, ${por[2]} com 3.`;
  }, [story, ordenados, sozinhos, duplas]);

  const rotulo = { fontSize: '0.8rem', fontWeight: 600 };
  const opcao = (atual, set, valores) => (
    <div className="d-flex gap-1 flex-wrap mb-2">
      {valores.map(([v, t]) => (
        <button key={v} type="button" className={`btn btn-sm ${atual === v ? 'btn-warning' : 'btn-outline-secondary'}`}
          onClick={() => set(v)}>{t}</button>
      ))}
    </div>
  );
  const semFotoQtd = produtos.filter(p => !p.temFoto && (quais === 'todos' || !jaUsados.has(p.codpro))).length;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onFechar}>
      <div className="card p-3" style={{ width: '95%', maxWidth: 640, maxHeight: '92vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h6 className="fw-bold mb-0"><i className="bi bi-magic me-2"></i>{story ? 'Montar stories automático' : 'Montar pôsteres automático'}</h6>
            <small className="text-muted">Os que mais vendem vêm primeiro{story ? ' e saem sozinhos, em destaque' : ''}. Depois dá pra ajustar tudo.</small>
          </div>
          <button className="btn btn-sm btn-outline-secondary" onClick={onFechar}><i className="bi bi-x-lg"></i></button>
        </div>

        <label className="form-label mb-1" style={rotulo}>Quais produtos</label>
        {opcao(quais, setQuais, [['faltam', `Só os que ainda não têm ${story ? 'story' : 'pôster'}`], ['todos', 'Todos da campanha']])}

        <label className="form-label mb-1" style={rotulo}>Destaque pra quem mais vende em</label>
        {opcao(metrica, setMetrica, [['valor', 'R$ vendido'], ['vendas', 'Nº de vendas']])}
        <small className="text-muted d-block mb-2" style={{ fontSize: '0.72rem' }}>Últimos 90 dias, todas as lojas.</small>

        <div className="d-flex gap-3 flex-wrap mb-2">
          <div>
            <label className="form-label mb-1" style={rotulo}>Usar só os mais vendidos</label>
            <input type="number" min="1" className="form-control form-control-sm" style={{ width: 120 }} value={limite}
              placeholder="todos" onChange={e => setLimite(e.target.value)} />
          </div>
          {story && <>
            <div>
              <label className="form-label mb-1" style={rotulo}>Sozinhos (destaque)</label>
              <input type="number" min="0" className="form-control form-control-sm" style={{ width: 100 }} value={sozinhos}
                onChange={e => setSozinhos(Math.max(0, parseInt(e.target.value, 10) || 0))} />
            </div>
            <div>
              <label className="form-label mb-1" style={rotulo}>De 2 em 2 (produtos)</label>
              <input type="number" min="0" step="2" className="form-control form-control-sm" style={{ width: 100 }} value={duplas}
                onChange={e => setDuplas(Math.max(0, parseInt(e.target.value, 10) || 0))} />
            </div>
          </>}
        </div>
        {story && <small className="text-muted d-block mb-2" style={{ fontSize: '0.72rem' }}>O resto vai de 3 em 3, alternando as disposições.</small>}

        {!story && <>
          <label className="form-label mb-1" style={rotulo}>Modelo</label>
          {opcao(modelo, setModelo, [['alternar', 'Alternar'], ['folha-cima', 'Folha em cima'], ['folha-baixo', 'Folha embaixo']])}
        </>}

        <div className="form-check mb-1">
          <input className="form-check-input" type="checkbox" id="auto-de" checked={mostrarDe} onChange={e => setMostrarDe(e.target.checked)} />
          <label className="form-check-label" htmlFor="auto-de" style={{ fontSize: '0.82rem' }}>Mostrar o "De:" quando tiver desconto</label>
        </div>
        <div className="form-check mb-1">
          <input className="form-check-input" type="checkbox" id="auto-semfoto" checked={semFoto} onChange={e => setSemFoto(e.target.checked)} />
          <label className="form-check-label" htmlFor="auto-semfoto" style={{ fontSize: '0.82rem' }}>
            Incluir produtos sem foto {semFotoQtd ? <span className="text-muted">({semFotoQtd})</span> : null}
          </label>
        </div>
        {temItens && (
          <div className="form-check mb-2">
            <input className="form-check-input" type="checkbox" id="auto-trocar" checked={substituir} onChange={e => setSubstituir(e.target.checked)} />
            <label className="form-check-label" htmlFor="auto-trocar" style={{ fontSize: '0.82rem' }}>
              Apagar os {story ? 'stories' : 'pôsteres'} que já estão montados (senão, entram depois deles)
            </label>
          </div>
        )}

        <div className="p-2 mb-2" style={{ background: '#f6f6f6', borderRadius: 6, fontSize: '0.8rem' }}>
          {!ranking
            ? <span><span className="spinner-border spinner-border-sm me-2"></span>Buscando as vendas dos últimos 90 dias...</span>
            : <>
                {erroVendas && <div className="text-danger mb-1">Não consegui buscar as vendas: a ordem fica a da lista.</div>}
                <b>{resumo}</b>
                {ordenados.length > 0 && (
                  <div className="text-muted mt-1" style={{ fontSize: '0.74rem' }}>
                    Mais vendidos: {ordenados.slice(0, 5).map(p => {
                      const v = ranking.get(p.codpro);
                      return `${p.nome.slice(0, 28)} (${metrica === 'valor' ? `R$ ${fmt(v?.valor)}` : `${fmt(v?.vendas)} vendas`})`;
                    }).join(' · ')}
                  </div>
                )}
              </>}
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-sm btn-outline-secondary" onClick={onFechar}>Cancelar</button>
          <button className="btn btn-sm btn-success" disabled={!ranking || !ordenados.length}
            onClick={() => onGerar({ produtos: ordenados, sozinhos, duplas, mostrarDe, substituir, modelo })}>
            <i className="bi bi-magic me-1"></i>Montar
          </button>
        </div>
      </div>
    </div>
  );
}
